import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services';
import type { LoyaltyCard } from '../../types';
import styles from './Dashboard.module.css';

export const Dashboard: React.FC = () => {
  const { customer, refreshCustomer, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedCard, setSelectedCard] = useState<LoyaltyCard | null>(
    customer?.loyaltyCards?.[0] ?? null
  );
  const [showAddCard, setShowAddCard] = useState(false);
  const [tenantCode, setTenantCode] = useState('');
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const handleAddCard = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    setAddLoading(true);
    try {
      await authService.addLoyaltyCard(tenantCode);
      await refreshCustomer();
      setShowAddCard(false);
      setTenantCode('');
    } catch (err: unknown) {
      setAddError(err instanceof Error ? err.message : 'Codice non valido');
    } finally {
      setAddLoading(false);
    }
  }, [tenantCode, refreshCustomer]);

  if (!customer) return null;

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.greeting}>Ciao, {customer.firstName}</h1>
          <p className={styles.email}>{customer.email}</p>
        </div>
        <button className={styles.logoutBtn} onClick={logout}>Esci</button>
      </header>

      {/* QR Code Section */}
      {selectedCard && (
        <div className={styles.qrSection}>
          <div className={styles.qrCard}>
            <p className={styles.qrLabel}>{selectedCard.companyName}</p>
            <div className={styles.qrCode}>
              <QRCodeSVG
                value={selectedCard.loyaltyCode}
                size={200}
                bgColor="transparent"
                fgColor="#FFFFFF"
                level="M"
              />
            </div>
            <p className={styles.loyaltyCode}>{selectedCard.loyaltyCode}</p>
            <p className={styles.qrHint}>Mostra questo codice al cassiere</p>
          </div>

          <div className={styles.balanceCard}>
            <span className={styles.balanceLabel}>I tuoi punti</span>
            <span className={styles.balanceValue}>{selectedCard.pointsBalance}</span>
          </div>

          <div className={styles.actions}>
            <button className={styles.actionBtn} onClick={() => navigate(`/prizes/${selectedCard.companyId}`)}>
              Catalogo Premi
            </button>
            <button className={styles.actionBtnSecondary} onClick={() => navigate(`/history/${selectedCard.companyId}`)}>
              Storico Punti
            </button>
          </div>
        </div>
      )}

      {/* Loyalty Cards */}
      <section className={styles.cardsSection}>
        <div className={styles.cardsHeader}>
          <h2>Le tue tessere</h2>
          <button className={styles.addBtn} onClick={() => setShowAddCard(true)}>
            + Aggiungi
          </button>
        </div>

        <div className={styles.cardsList}>
          {customer.loyaltyCards.map((card) => (
            <button
              key={card.id}
              className={`${styles.loyaltyCardBtn} ${selectedCard?.id === card.id ? styles.loyaltyCardActive : ''}`}
              onClick={() => setSelectedCard(card)}
            >
              <span className={styles.cardName}>{card.companyName}</span>
              <span className={styles.cardPoints}>{card.pointsBalance} pt</span>
            </button>
          ))}

          {customer.loyaltyCards.length === 0 && (
            <p className={styles.noCards}>
              Nessuna tessera attiva. Aggiungi il codice di un negozio per iniziare!
            </p>
          )}
        </div>
      </section>

      {/* Add Card Modal */}
      {showAddCard && (
        <div className={styles.overlay} onClick={() => setShowAddCard(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Aggiungi Negozio</h3>
            <p className={styles.modalHint}>Inserisci il codice del negozio per iscriverti al suo programma fedelta'</p>
            <form onSubmit={handleAddCard}>
              {addError && <div className={styles.error}>{addError}</div>}
              <input
                type="text"
                value={tenantCode}
                onChange={(e) => setTenantCode(e.target.value.toUpperCase())}
                placeholder="Codice negozio (es. BAR-ROMA-01)"
                required
                disabled={addLoading}
                className={styles.modalInput}
              />
              <div className={styles.modalActions}>
                <button type="button" className={styles.modalCancel} onClick={() => setShowAddCard(false)}>
                  Annulla
                </button>
                <button type="submit" className={styles.modalSubmit} disabled={addLoading}>
                  {addLoading ? 'Aggiunta...' : 'Aggiungi'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
